import React, { useEffect, useMemo } from 'react';
import LanguageBarItem from 'components/LanguageBarItem/LanguageBarItem';
import { TLanguageIDs, ITags } from 'types';
import { trueOrderLanguageIDs } from 'appConstants';
import style from './LanguageBar.module.scss';

interface ILanguageBar {
	setSelectedLanguage: (value: Nullable<string>) => void;
	onClickHideButton: () => void;
	selectedLanguage: Nullable<string>;
	IDSelectedCustomerDefaultLanguage: Nullable<string>;
	IDsSelectedCustomerLanguages: string[];
	tags: ITags[];
	isShowAdditionalTags: boolean;
}

const LanguageBar: React.FC<ILanguageBar> = ({
	setSelectedLanguage,
	onClickHideButton,
	selectedLanguage,
	IDSelectedCustomerDefaultLanguage,
	IDsSelectedCustomerLanguages,
	tags,
	isShowAdditionalTags,
}) => {
	const languageIDsWithEmptyTags = useMemo(
		() => tags.filter((el) => el.tags.includes('')).map((el) => el.languageId),
		[tags],
	);

	const sortedLanguages = useMemo(() => {
		const languages = [...IDsSelectedCustomerLanguages];
		const sortByObject: { [key: string]: number } = trueOrderLanguageIDs.reduce((obj, item, index) => {
			return {
				...obj,
				[item]: index,
			};
		}, {});
		languages.sort((a, b) => sortByObject[a] - sortByObject[b]);
		languages.sort((a) => (a === IDSelectedCustomerDefaultLanguage ? -1 : 1));
		return languages;
	}, [IDsSelectedCustomerLanguages, IDSelectedCustomerDefaultLanguage]);

	useEffect(() => {
		if (IDSelectedCustomerDefaultLanguage && sortedLanguages.includes(IDSelectedCustomerDefaultLanguage)) {
			setSelectedLanguage(IDSelectedCustomerDefaultLanguage);
		} else if (sortedLanguages.length) {
			setSelectedLanguage(sortedLanguages[0]);
		} else {
			setSelectedLanguage(null);
		}
	}, [sortedLanguages.length, IDSelectedCustomerDefaultLanguage]);

	useEffect(() => {
		if (
			selectedLanguage &&
			languageIDsWithEmptyTags.includes(selectedLanguage as TLanguageIDs) &&
			!isShowAdditionalTags &&
			tags[0]?.tags.length > 3
		) {
			onClickHideButton();
		}
	}, [selectedLanguage]);

	return (
		<>
			{IDsSelectedCustomerLanguages.length && tags[0]?.tags.length ? (
				<div className={style.container} data-test="LanguageBar">
					{sortedLanguages.map((el) => {
						const itemHasError = languageIDsWithEmptyTags.includes(el as TLanguageIDs);

						return (
							<LanguageBarItem
								key={el}
								onClick={() => setSelectedLanguage(el)}
								languageId={el as TLanguageIDs}
								isActive={selectedLanguage === el}
								isDefault={IDSelectedCustomerDefaultLanguage === el}
								itemHasError={itemHasError}
							/>
						);
					})}
				</div>
			) : (
				<div />
			)}
		</>
	);
};

export default React.memo(LanguageBar);
